import { useMemo, useState } from 'react';
import { ExercisePlayer } from './ExercisePlayer';
import { useEngine } from '../../hooks/useEngine';
import { computeNextReview, isDue } from '../../lib/storage/review';
import { saveAttempt, updateExercise } from '../../lib/storage/db';
import { hashString } from '../../lib/pgn/parse';
import type { AttemptOutcome, Exercise, ExerciseAttempt, StoredGame } from '../../types';

export interface ExercisesTabProps {
  exercises: Exercise[];
  games: StoredGame[];
  onChanged: () => void;
}

function gameLabel(games: StoredGame[], gameId: string): string {
  const g = games.find((s) => s.game.id === gameId);
  if (!g) return 'Partie';
  return `${g.game.headers.White ?? '?'} vs ${g.game.headers.Black ?? '?'}${g.game.isDemo ? ' (démo)' : ''}`;
}

export function ExercisesTab({ exercises, games, onChanged }: ExercisesTabProps) {
  const { engine } = useEngine();
  const [activeId, setActiveId] = useState<string | null>(null);

  const dueExercises = useMemo(() => exercises.filter((e) => isDue(e.review)), [exercises]);
  const active = exercises.find((e) => e.id === activeId) ?? null;

  async function handleCompleted(exercise: Exercise, outcome: AttemptOutcome, hintsUsed: number, playedUci?: string) {
    const attempt: ExerciseAttempt = {
      id: hashString(`${exercise.id}:${Date.now()}:${Math.random()}`),
      exerciseId: exercise.id,
      timestamp: new Date().toISOString(),
      outcome,
      hintsUsed,
      playedUci,
    };
    await saveAttempt(attempt);
    const updated: Exercise = { ...exercise, review: computeNextReview(exercise.review, outcome) };
    await updateExercise(updated);
    onChanged();
  }

  if (exercises.length === 0) {
    return (
      <section className="panel">
        <h2>Mes exercices</h2>
        <p className="muted">
          Aucun exercice pour l'instant. Analyse une partie dans l'onglet « Ma partie » : Magnus proposera jusqu'à
          trois exercices tirés de ses moments les plus pertinents.
        </p>
      </section>
    );
  }

  if (active) {
    return (
      <section className="panel">
        <button type="button" className="link-btn" onClick={() => setActiveId(null)}>
          ← Retour à la liste
        </button>
        <h2>Exercice — {gameLabel(games, active.gameId)}</h2>
        {active.theme && <p className="muted">Thème : {active.theme.replace(/_/g, ' ')}</p>}
        <ExercisePlayer
          key={active.id}
          exercise={active}
          engine={engine}
          onCompleted={(outcome, hintsUsed, uci) => handleCompleted(active, outcome, hintsUsed, uci)}
        />
      </section>
    );
  }

  return (
    <section className="panel">
      <h2>Mes exercices</h2>

      {dueExercises.length > 0 ? (
        <>
          <h3>À réviser aujourd'hui ({dueExercises.length})</h3>
          <ExerciseList exercises={dueExercises} games={games} onSelect={setActiveId} />
        </>
      ) : (
        <p className="muted">Rien à réviser aujourd'hui. Reviens plus tard, ou entraîne-toi librement ci-dessous.</p>
      )}

      <h3>Tous les exercices ({exercises.length})</h3>
      <ExerciseList exercises={exercises} games={games} onSelect={setActiveId} />
    </section>
  );
}

function ExerciseList({
  exercises,
  games,
  onSelect,
}: {
  exercises: Exercise[];
  games: StoredGame[];
  onSelect: (id: string) => void;
}) {
  return (
    <ul className="exercise-list">
      {exercises.map((e) => (
        <li key={e.id}>
          <button type="button" className="exercise-item" onClick={() => onSelect(e.id)}>
            <span>{gameLabel(games, e.gameId)}</span>
            <span className="muted">
              {e.theme ? e.theme.replace(/_/g, ' ') : 'Position à améliorer'} · {e.playerColor === 'w' ? 'Blancs' : 'Noirs'} au
              trait
            </span>
            <span className="muted small">
              Prochaine révision : {new Date(e.review.dueAt).toLocaleDateString('fr-FR')}
              {e.review.streak > 0 ? ` · ${e.review.streak} succès d'affilée` : ''}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}
